/**
 * An evaluation copy of a case (05 §1, decisions 3–7): a fresh request in
 * the case's family, live in the Workbench, nothing going to the Backoffice,
 * the client never seeing it. Made on a click, with a confirm; the page
 * then opens the copy.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Button, Group, Modal, Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconCopy } from '@tabler/icons-react';

import * as Sentry from '@sentry/react';

import { ApiConflictError, vnextApiClient } from '@/services/vnext-api';
import { casePathOf, type WorkbenchCopyMade } from '@/types/workbench';
import { toError } from '@/utils/errors';

interface CopyIntoWorkbenchButtonProps {
  reportId: string;
  size?: 'xs' | 'sm';
  variant?: 'default' | 'light' | 'subtle';
  onMade?: (made: WorkbenchCopyMade) => void | Promise<void>;
}

export function CopyIntoWorkbenchButton({
  reportId,
  size = 'xs',
  variant = 'default',
  onMade,
}: Readonly<CopyIntoWorkbenchButtonProps>) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const confirm = async () => {
    setBusy(true);
    try {
      const made = await vnextApiClient.copyIntoWorkbench(reportId);
      notifications.show({
        color: 'teal',
        message: `Copied into the Workbench as ${made.codename ?? 'a new case'} — ${made.files} file${made.files === 1 ? '' : 's'}, the setup run queued.`,
      });
      setOpen(false);
      await onMade?.(made);
      navigate(casePathOf(made.familyKey, made.reportId) ?? '/workbench');
    } catch (err) {
      if (!(err instanceof ApiConflictError)) {
        Sentry.captureException(toError(err));
      }
      notifications.show({ color: 'red', message: toError(err, 'Could not copy it').message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button
        size={size}
        variant={variant}
        leftSection={<IconCopy size={14} />}
        onClick={() => setOpen(true)}
        data-testid="copy-into-workbench"
      >
        Copy into Workbench
      </Button>
      <Modal
        opened={open}
        onClose={() => setOpen(false)}
        title="Copy this case into the Workbench?"
        centered
      >
        <Stack gap="sm">
          <Text size="sm">
            An evaluation copy: a fresh request in this case&apos;s family, set up and worked in the
            Workbench beside it. Nothing goes to the Backoffice, and the client never sees it. Take
            the live case&apos;s partner returns from its Documents tab.
          </Text>
          <Group justify="flex-end" gap="xs">
            <Button size="xs" variant="subtle" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              size="xs"
              loading={busy}
              onClick={() => void confirm()}
              data-testid="confirm-copy-into-workbench"
            >
              Copy into Workbench
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
